import { useCallback, useEffect, useRef, useState } from 'react';
import type { CardTxnRow, PosTerminal } from '@kassa/shared';
import { api, ApiError } from '../api';
import { adapterFor } from '../pos/adapters';
import { payByCard } from '../pos/flow';

// Kart hissəsi terminala (KT-91): terminal printer kimi seçilir (kassanın terminalı əvvəlcədən seçili), məbləğ terminala gedir,
// «Terminalda kartı oxudun». Təsdiq — satış bu əməliyyatla yazılır; rədd — kassir başqa üsul seçir; yoxlanır — menecer.
// Terminal siyahısı yüklənənə qədər satış bağlıdır (pul terminalda çıxmadan «kart» yazılmasın). Terminalı olmayan kassada —
// kart əl ilə (köhnə qayda); «manual» terminal açıq seçiləndə server də bunu bilir.

export interface CardTerminalState {
  /** Terminal siyahısı yüklənib (və ya kassada terminal yoxdur). */
  ready: boolean;
  /** İnteqrasiyalı terminal seçilib — kart təsdiqsiz göndərilmir. */
  active: boolean;
  approved: CardTxnRow | null;
  /** İnteqrasiyasız («manual») terminal açıq seçilib. */
  manualTerminalId: string | null;
}

export const CARD_TERMINAL_INITIAL: CardTerminalState = { ready: false, active: false, approved: null, manualTerminalId: null };

const errText = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Xəta baş verdi');

export function CardTerminal({ registerId, amount, state, onChange }: { registerId: string; amount: number; state: CardTerminalState; onChange: (s: CardTerminalState) => void }) {
  const [terminals, setTerminals] = useState<PosTerminal[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [terminalId, setTerminalId] = useState('');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<CardTxnRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const attempt = useRef(crypto.randomUUID());
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    if (!registerId) return;
    let ignore = false; // köhnə kassanın cavabı yeni kassanın siyahısını əzməsin
    setTerminals(null);
    setLoadError(null);
    api
      .posForRegister(registerId)
      .then((r) => {
        if (ignore) return;
        setTerminals(r.terminals);
        setTerminalId(r.defaultTerminalId ?? r.terminals.find((t) => t.adapter !== 'manual')?.id ?? r.terminals[0]?.id ?? '');
      })
      .catch((e: unknown) => {
        if (ignore) return;
        // 403 — kassirin terminal icazəsi yoxdur: köhnə qayda (əl ilə). Başqa xəta — satış bağlı qalır, yenidən yüklə
        if (e instanceof ApiError && e.status === 403) setTerminals([]);
        else setLoadError(errText(e));
      });
    return () => {
      ignore = true;
    };
  }, [registerId, seq]);

  const terminal = terminals?.find((t) => t.id === terminalId) ?? null;
  const adapter = terminal ? adapterFor(terminal) : null;
  const next: CardTerminalState = {
    ready: terminals !== null,
    active: adapter !== null,
    approved: adapter !== null ? state.approved : null,
    manualTerminalId: terminal && terminal.adapter === 'manual' ? terminal.id : null,
  };
  const emit = useCallback(onChange, [onChange]);
  useEffect(() => {
    if (next.ready !== state.ready || next.active !== state.active || next.manualTerminalId !== state.manualTerminalId || next.approved !== state.approved) emit(next);
  });
  // Unmount (kart 0 oldu): yenidən qurulanda siyahı gələnə qədər satış bağlı qalsın
  useEffect(() => () => emit({ ...CARD_TERMINAL_INITIAL }), [emit]);

  const amountStr = amount.toFixed(2);
  const amountAz = amountStr.replace('.', ',');

  if (loadError) {
    return (
      <div className="card-terminal">
        <p className="bad" role="alert">
          Terminal siyahısı yüklənmədi: {loadError}. Kart ödənişi terminal olmadan yazılmır.
        </p>
        <button type="button" className="btn-secondary" onClick={() => setSeq((n) => n + 1)}>
          Yenidən yüklə
        </button>
      </div>
    );
  }
  if (terminals === null) return <p className="muted small">Terminallar yüklənir…</p>;
  if (terminals.length === 0) return null;

  const send = async () => {
    if (!terminal || !adapter || amount <= 0) return;
    setBusy(true);
    setError(null);
    try {
      const row = await payByCard(api, adapter, { id: attempt.current, terminalId: terminal.id, registerId, amount: amountStr });
      setLast(row);
      if (row.status === 'declined') attempt.current = crypto.randomUUID(); // rədd — növbəti cəhdin öz UUID-si
      onChange({ ...next, approved: row.status === 'approved' ? row : null });
    } catch (e) {
      // Server cavab vermədi: eyni cəhd id-si saxlanır — yenidən basanda status soruşulur, iki dəfə çıxmır
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const review = last?.status === 'needs_review';
  return (
    <div className="card-terminal" aria-live="polite">
      <label>
        Terminal
        <select value={terminalId} onChange={(e) => setTerminalId(e.target.value)} disabled={busy || state.approved !== null || review}>
          {terminals.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
              {t.bank ? ` · ${t.bank}` : ''}
              {t.adapter === 'manual' ? ' (əl ilə)' : ''}
            </option>
          ))}
        </select>
      </label>
      {next.active && state.approved === null && !review && (
        <button type="button" className="btn-secondary" disabled={busy || amount <= 0} onClick={() => void send()}>
          {busy ? 'Terminalda kartı oxudun…' : `Terminala göndər (${amountAz} ₼)`}
        </button>
      )}
      {next.manualTerminalId && <p className="muted small">İnteqrasiyasız terminal: məbləği terminalda özünüz yığın.</p>}
      {state.approved && (
        <p className="ok" role="status">
          Təsdiq · **** {state.approved.cardLast4 ?? '????'} · RRN {state.approved.rrn}
        </p>
      )}
      {last?.status === 'declined' && state.approved === null && (
        <p className="bad" role="alert">
          Terminal rədd etdi{last.message ? `: ${last.message}` : ''}. Başqa üsul seçin.
        </p>
      )}
      {review && (
        <p className="bad" role="alert">
          Terminalın cavabı bilinmir — əməliyyat yoxlanır (menecer). Təkrar göndərmək olmaz: pul çıxıbsa ikinci dəfə çıxar. Başqa üsul seçin və ya menecerin
          həllini gözləyin.
        </p>
      )}
      {error && (
        <p className="bad" role="alert">
          {error} — yenidən basın: eyni əməliyyatın statusu soruşulacaq (iki dəfə çıxmır).
        </p>
      )}
    </div>
  );
}
